import { useTranslation } from 'react-i18next';
import { Download, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';
import type { ExportHistoryItem } from '@/api/api';
import { toast } from 'sonner';
import i18n from '@/i18n';

interface ExportHistoryItemProps {
  item: ExportHistoryItem;
  onRefresh?: () => void;
}

/**
 * 状态颜色映射
 */
const statusColors: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'secondary',
  processing: 'default',
  completed: 'default',
  success: 'default',
  failed: 'destructive',
};

/**
 * Export History 列表项组件
 * 显示导出任务信息，支持点击下载
 */
export function ExportHistoryItem({ item, onRefresh }: ExportHistoryItemProps) {
  const { t } = useTranslation('chrome');

  const getStatusLabel = (s: string): string => {
    const map: Record<string, string> = {
      pending: t('exportHistory.statusPending'),
      processing: t('exportHistory.statusProcessing'),
      completed: t('exportHistory.statusCompleted'),
      success: t('exportHistory.statusCompleted'),
      failed: t('exportHistory.statusFailed'),
    };
    return map[s] ?? s;
  };

  const getEntityTypeName = (et: string): string => {
    const map: Record<string, string> = {
      user: t('exportHistory.entityTypeUser'),
      listing: t('exportHistory.entityTypeListing'),
      reservation: t('exportHistory.entityTypeReservation'),
      task: t('exportHistory.entityTypeTask'),
      guest: t('exportHistory.entityTypeGuest'),
      review: t('exportHistory.entityTypeReview'),
      intent: t('exportHistory.entityTypeIntent'),
    };
    return map[et] ?? et;
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!item.downloadUrl) {
      toast.error(i18n.t('chrome:exportHistory.toastNoUrl'));
      return;
    }

    // 检查 URL 是否过期
    if (item.expiredAt) {
      const expiredTime = new Date(item.expiredAt);
      const now = new Date();
      if (now > expiredTime) {
        toast.error(i18n.t('chrome:exportHistory.toastExpired'));
        // 自动刷新列表以获取新的下载链接
        if (onRefresh) {
          onRefresh();
        }
        return;
      }
    }

    try {
      // 尝试使用 fetch 下载（支持错误处理和自定义文件名）
      try {
        const response = await fetch(item.downloadUrl);
        if (!response.ok) {
          // 检查是否是过期错误
          const responseText = await response.text();
          if (responseText.includes('AccessDenied') || responseText.includes('expired')) {
            toast.error(i18n.t('chrome:exportHistory.toastExpired'));
            // 自动刷新列表以获取新的下载链接
            if (onRefresh) {
              onRefresh();
            }
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = item.filename || 'export.csv';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        toast.success(i18n.t('chrome:exportHistory.toastSuccess'));
      } catch {
        // 如果 fetch 失败（如 CORS 错误或过期），降级使用 <a> 标签直接打开
        const link = document.createElement('a');
        link.href = item.downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = item.filename || 'export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 给用户一个短暂的视觉反馈
        await new Promise(resolve => setTimeout(resolve, 500));
        toast.success(i18n.t('chrome:exportHistory.toastStarted'));
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(i18n.t('chrome:exportHistory.toastFailed'));
    }
  };

  const status = item.status || 'pending';
  const isCompleted = status === 'completed' || status === 'success';
  const isProcessing = status === 'processing';
  const isPending = status === 'pending';
  const isFailed = status === 'failed';

  // 格式化创建时间
  const formattedDate = item.createdAt
    ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })
    : t('exportHistory.dateUnknown');

  // 获取实体类型显示名称
  const entityTypeName = item.entityType
    ? getEntityTypeName(item.entityType)
    : t('exportHistory.entityTypeUnknown');

  return (
    <div
      className={`flex items-start gap-3 px-4 py-2.5 hover:bg-accent rounded-md transition-colors ${
        isCompleted && item.downloadUrl ? 'cursor-pointer' : ''
      }`}
      onClick={isCompleted && item.downloadUrl ? handleDownload : undefined}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          {item.filename && (
            <span className="text-sm font-medium truncate flex-1 min-w-0">{item.filename}</span>
          )}
          <Badge variant={statusColors[status] || 'secondary'} className="text-xs shrink-0 whitespace-nowrap">
            {getStatusLabel(status)}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{entityTypeName}</span>
          <span>•</span>
          <span>{formattedDate}</span>
        </div>
      </div>
      <div className="shrink-0 pt-0.5">
        {isPending || isProcessing ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : isFailed ? (
          <AlertCircle className="h-4 w-4 text-destructive" />
        ) : isCompleted && item.downloadUrl ? (
          <Download className="h-4 w-4 text-primary" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </div>
  );
}
